import { httpGetAuthorized } from "components/utils/http";
import { isInIframe } from "components/utils/dom";
import { State } from "components/utils/useStates";
import { trim } from "lodash";
import useSWR from "swr/immutable";

export const ADDRESS_LOOKUP_MIN_QUERY_LENGTH = 3;

export interface MelissaApiAddress {
    addressLine1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    countrySubdivisionCode?: string;
    suiteList?: string[];
    suiteName?: string;
}

const SECONDARY_ADDRESS_PATTERN = /\b(?:apartment|apt|suite|ste|unit|#|floor|fl|room|rm|building|bldg)\.?\s+\S.*$/i;

export interface MelissaContactAddressFields {
    address: string;
    address_cont: string;
    city: string;
    state: string;
    zip: string;
}

export const mapMelissaAddressToContactFields = (address: MelissaApiAddress, states: State[] = []): MelissaContactAddressFields => {
    const line1 = (address.addressLine1 ?? "").trim();
    const suiteListValue = address.suiteList?.find((suite) => suite.trim())?.trim() ?? "";
    const suiteName = (address.suiteName ?? "").trim();
    const secondaryValue = suiteListValue || suiteName;
    const addressFields = {
        city: address.city ?? "",
        state: resolveStateValue(address.state || address.countrySubdivisionCode || "", states),
        zip: address.postalCode ?? "",
    };

    if (!line1) {
        return { address: "", address_cont: secondaryValue, ...addressFields };
    }

    if (suiteListValue) {
        return { ...stripSecondaryAddress(line1, suiteListValue), ...addressFields };
    }

    const secondaryMatch = new RegExp(SECONDARY_ADDRESS_PATTERN).exec(line1);
    const primaryBeforeMatch = secondaryMatch?.index !== undefined ? trimSeparators(line1.slice(0, secondaryMatch.index)) : "";
    if (secondaryMatch && primaryBeforeMatch) {
        return {
            address: primaryBeforeMatch,
            address_cont: secondaryMatch[0].trim(),
            ...addressFields,
        };
    }

    if (!suiteName) {
        return { address: line1, address_cont: "", ...addressFields };
    }

    return { ...stripSecondaryAddress(line1, suiteName), ...addressFields };
};

const trimSeparators = (value: string): string => trim(trim(value, ",- ").trim(), ",- ");

const stripSecondaryAddress = (line1: string, secondaryValue: string): { address: string; address_cont: string } => {
    const secondaryIndex = line1.toLowerCase().lastIndexOf(secondaryValue.toLowerCase());

    if (secondaryIndex === -1) {
        return { address: line1, address_cont: secondaryValue };
    }

    const beforeSecondary = trimSeparators(line1.slice(0, secondaryIndex));
    const afterSecondary = trimSeparators(line1.slice(secondaryIndex + secondaryValue.length));

    return {
        address: beforeSecondary || afterSecondary || line1,
        address_cont: secondaryValue,
    };
};

const resolveStateValue = (state: string, states: State[]): string => {
    const candidate = state.trim();
    if (!candidate) {
        return "";
    }

    const upper = candidate.toUpperCase();
    const match = states.find((s) => s.abbreviation?.toUpperCase() === upper || s.state?.toUpperCase() === upper);
    return match?.abbreviation ?? candidate;
};

interface MelissaApiResult {
    address?: MelissaApiAddress;
}

interface UseAddressLookupOptions {
    onResult?: (results: MelissaApiAddress[]) => void;
}

const toAddresses = (data: MelissaApiResult[] | undefined): MelissaApiAddress[] =>
    (data ?? []).map((result) => result.address).filter((address): address is MelissaApiAddress => !!address);

export const useAddressLookup = (
    query: string,
    options?: UseAddressLookupOptions
): [results: MelissaApiAddress[], isLoading: boolean, error: any] => {
    const trimmed = query.trim();

    let url: string | null = null;
    if (!isInIframe() && trimmed.length >= ADDRESS_LOOKUP_MIN_QUERY_LENGTH) {
        const params = new URLSearchParams({ address: trimmed });
        url = `${process.env.REACT_APP_ADDRESS_LOOKUP_ENDPOINT}?${params.toString()}`;
    }

    const { data, error, isLoading } = useSWR<MelissaApiResult[]>(url, httpGetAuthorized, {
        onSuccess: (responseData) => options?.onResult?.(toAddresses(responseData)),
    });

    return [toAddresses(data), isLoading, error];
};
